/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {SearchField} from '../components/search/SearchField';

interface ColumnSearchProps {
  setSearchTerm: Function;
};

// defines the column chooser style definitions ...
const useStyles = makeStyles(theme => ({
  searchBox : {
    padding : '0 30px 5px'
  }
}));

/**
 * @ColumnSearch
 *
 * The ColumnSearch provides a UI to filter the columns. When the user types in
 * the search field, the column will be filtered to show the matching term.
 *
 * ```typescript
 *
 *    <ColumnSearch setSearchTerm={setSearchTerm} />
 * ```
 *
 * @see ColumnSearch
 */
export const ColumnSearch = ({setSearchTerm}: ColumnSearchProps) => {
  const classes = useStyles();

  const debouncedSetSearchTerm = useCallback(
    debounce((value: string) => setSearchTerm(value), 250),
    [setSearchTerm]
  );

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    debouncedSetSearchTerm(e.target.value);
  }, [debouncedSetSearchTerm]);

  return (
    <div className={classes.searchBox}>
      <SearchField placeholder={translate('common:columnChooser.searchColumnPlaceholder')}
        onChange={handleSearchChange} />
    </div>
  );
};

export default React.memo(ColumnSearch);
